import { createAsyncThunk } from '@reduxjs/toolkit';
import api, { handleApiError } from 'state/api';

export const getStoresApi = createAsyncThunk(
  'stores/getStoresApi',
  async (
    { page = 1, limit = 10, search = '', marketplace = '', supplier = '', status = '' },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) params.append('search', search);
      if (marketplace) params.append('marketplace', marketplace);
      if (supplier) params.append('supplier', supplier);
      if (status) params.append('status', status);

      const response = await api.get(`stores?${params.toString()}`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getStoresApi', error, rejectWithValue);
    }
  },
);

export const getStoreApi = createAsyncThunk(
  'stores/getStore',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`stores/${id}`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getStoreApi', error, rejectWithValue);
    }
  },
);

export const createStoreApi = createAsyncThunk(
  'stores/createStore',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('stores', data);
      return response.data;
    } catch (error) {
      return handleApiError('createStoreApi', error, rejectWithValue);
    }
  },
);

export const updateStoreApi = createAsyncThunk(
  'stores/updateStore',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`stores/${id}`, data);
      return response.data;
    } catch (error) {
      return handleApiError('updateStoreApi', error, rejectWithValue);
    }
  },
);

export const getStoresListApi = createAsyncThunk(
  'stores/getStoresListApi',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('stores/list');
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getStoresListApi', error, rejectWithValue);
    }
  },
);

export const updateRefreshTokenApi = createAsyncThunk(
  'stores/updateRefreshToken',
  async ({ state, code }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        state,
        code,
      });

      const response = await api.put(`stores/updateRefreshToken?${params.toString()}`);
      return response.data;
    } catch (error) {
      return handleApiError('updateRefreshTokenApi', error, rejectWithValue);
    }
  },
);

export const uploadDescriptionTemplateApi = createAsyncThunk(
  'stores/uploadDescriptionTemplate',
  async ({ storeId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        `stores/${storeId}/upload-description-template`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return response.data;
    } catch (error) {
      return handleApiError('uploadDescriptionTemplateApi', error, rejectWithValue);
    }
  },
);

export const deleteStoreApi = createAsyncThunk(
  'stores/deleteStore',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`stores/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError('deleteStoreApi', error, rejectWithValue);
    }
  },
);

export const loadListingsApi = createAsyncThunk(
  'stores/loadListings',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.post(`stores/load-listings/${storeId}`);
      return response.data;
    } catch (error) {
      return handleApiError('loadListingsApi', error, rejectWithValue);
    }
  },
);

export const getStorePoliciesApi = createAsyncThunk(
  'stores/getStorePolicies',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.get(`stores/${storeId}/policies`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getStorePoliciesApi', error, rejectWithValue);
    }
  },
);

export const updateStoreDefaultPoliciesApi = createAsyncThunk(
  'stores/updateStoreDefaultPolicies',
  async ({ storeId, ...body }, { rejectWithValue }) => {
    try {
      const response = await api.put(`stores/${storeId}/policies/defaults`, body);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('updateStoreDefaultPoliciesApi', error, rejectWithValue);
    }
  },
);

export const refreshStorePoliciesApi = createAsyncThunk(
  'stores/refreshStorePolicies',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.post(`stores/${storeId}/policies/refresh`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('refreshStorePoliciesApi', error, rejectWithValue);
    }
  },
);

export const getPoliciesRefreshStatusApi = createAsyncThunk(
  'stores/getPoliciesRefreshStatus',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.get(`stores/${storeId}/policies/refresh-status`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getPoliciesRefreshStatusApi', error, rejectWithValue);
    }
  },
);

export const getPoliciesProcessStatusApi = createAsyncThunk(
  'stores/getPoliciesProcessStatus',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.get(`stores/${storeId}/policies/process-status`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getPoliciesProcessStatusApi', error, rejectWithValue);
    }
  },
);

export const startPoliciesBulkUpdateApi = createAsyncThunk(
  'stores/startPoliciesBulkUpdate',
  async ({ storeId, ...body }, { rejectWithValue }) => {
    try {
      const response = await api.post(`stores/${storeId}/policies/bulk-update`, body);
      return response.data;
    } catch (error) {
      return handleApiError('startPoliciesBulkUpdateApi', error, rejectWithValue);
    }
  },
);

export const stopPoliciesBulkUpdateApi = createAsyncThunk(
  'stores/stopPoliciesBulkUpdate',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.post(`stores/${storeId}/policies/bulk-update/stop`);
      return response.data;
    } catch (error) {
      return handleApiError('stopPoliciesBulkUpdateApi', error, rejectWithValue);
    }
  },
);

export const downloadPoliciesRunReportCsvApi = createAsyncThunk(
  'stores/downloadPoliciesRunReportCsv',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.get(`stores/${storeId}/policies/run-report/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `store-policies-run-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { downloaded: true };
    } catch (error) {
      return handleApiError('downloadPoliciesRunReportCsvApi', error, rejectWithValue);
    }
  },
);

export const getLoadListingsStatusApi = createAsyncThunk(
  'stores/getLoadListingsStatus',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.get(`stores/${storeId}/load-listings/status`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getLoadListingsStatusApi', error, rejectWithValue);
    }
  },
);

export const updateLoadListingsScheduleApi = createAsyncThunk(
  'stores/updateLoadListingsSchedule',
  async ({ storeId, schedule }, { rejectWithValue }) => {
    try {
      const response = await api.put(`stores/${storeId}/load-listings/schedule`, schedule);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('updateLoadListingsScheduleApi', error, rejectWithValue);
    }
  },
);

export const downloadLoadListingsRunReportCsvApi = createAsyncThunk(
  'stores/downloadLoadListingsRunReportCsv',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.get(`stores/${storeId}/load-listings/run-report/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `load-listings-run-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { downloaded: true };
    } catch (error) {
      return handleApiError('downloadLoadListingsRunReportCsvApi', error, rejectWithValue);
    }
  },
);
